import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Loader2, RefreshCw, Send, CheckCircle2, DollarSign, Target, Mail, X,
  FileSpreadsheet, ExternalLink,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(/0$/, "")}%`);
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("sterilemate_token") || localStorage.getItem("token")}` } });

export default function RebateProgramDeskPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [statement, setStatement] = useState(null);
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState(null);
  const now = new Date();
  const [period, setPeriod] = useState(
    `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`);

  const periodOptions = (() => {
    const out = [];
    let y = now.getFullYear() + 1;
    let q = 4;
    for (let i = 0; i < 12; i += 1) {
      out.push(`${y}-Q${q}`);
      q -= 1;
      if (q === 0) { q = 4; y -= 1; }
    }
    return out;
  })();

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const myId = reqId.current + 1;
    reqId.current = myId;
    setLoading(true); setErr("");
    const [py, pq] = period.split("-Q");
    try {
      const r = await axios.get(
        `${API}/api/admin/rebates/accounts?year=${py}&quarter=${pq}`, auth());
      if (reqId.current !== myId) return;   // a newer period request won
      setData(r.data);
    } catch (e) {
      if (reqId.current !== myId) return;
      setErr(e?.response?.data?.detail || "Could not load rebate accounts");
    } finally {
      if (reqId.current === myId) setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const toastTimer = useRef(null);
  const showToast = (kind, msg, ms) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ kind, msg });
    toastTimer.current = setTimeout(() => setToast(null), ms);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const act = async (fn, key, okMsg) => {
    setBusy(key);
    try {
      await fn();
      showToast("ok", okMsg, 4000);
      await load();
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Action failed", 6000);
    } finally {
      setBusy("");
    }
  };

  const downloadLedger = async () => {
    setBusy("csv");
    const [py, pq] = period.split("-Q");
    try {
      const r = await axios.get(
        `${API}/api/admin/rebates/ledger.csv?year=${py}&quarter=${pq}`,
        { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rebate-ledger-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("ok", "Ledger downloaded", 3000);
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Could not build the ledger", 6000);
    } finally {
      setBusy("");
    }
  };

  const linkHospital = (row) => {    const v = window.prompt(
      `Link ${row.hospital_name} to a hospital account ID.\n\n` +
      `Until it is linked we cannot attribute contracted-line or backorder spend, so no rebate can accrue.`, "");
    if (!v) return;
    return act(
      () => axios.patch(`${API}/api/admin/rebates/enrollments/${row.enrollment_id}`,
        { hospital_id: v.trim() }, auth()),
      `lk-${row.enrollment_id}`, "Hospital account linked");
  };

  const approve = (row) => act(
    () => axios.patch(`${API}/api/admin/rebates/enrollments/${row.enrollment_id}`,
      { status: row.status === "active" ? "paused" : "active" }, auth()),
    `st-${row.enrollment_id}`,
    row.status === "active" ? "Enrollment paused" : "Enrollment activated");

  const verify = (row) => act(
    () => axios.patch(`${API}/api/admin/rebates/enrollments/${row.enrollment_id}`,
      { commitment_verified_for: data.period, commitment_verified: !row.commitment_verified }, auth()),
    `cv-${row.enrollment_id}`,
    row.commitment_verified ? "Commitment unverified" : "Primary-source commitment verified");

  const toggleRebate = (row) => {
    const turningOff = row.rebate_enabled;
    if (turningOff && !window.confirm(
      `Turn the rebate OFF for ${row.hospital_name}?\n\nThis takes effect from the NEXT quarter — ` +
      `accrual already earned in ${data.period} is still owed. Switching it off mid-quarter would ` +
      `break the fixed-terms requirement of the discount safe harbor.`)) return;
    return act(
      () => axios.patch(`${API}/api/admin/rebates/enrollments/${row.enrollment_id}`,
        { rebate_enabled: !row.rebate_enabled }, auth()),
      `rb-${row.enrollment_id}`,
      turningOff ? "Rebate switched off for this account" : "Rebate switched on");
  };

  const setMargin = (row) => {
    const pr = row.margin?.pricing || {};
    const askTrack = (track, label) => {
      const t = pr[track] || {};
      const v = window.prompt(
        `${label} MARKUP on cost for ${row.hospital_name} in ${data.period} (%).\n\n` +
        `Internal only — never shown to the hospital.\n` +
        `Standard ${t.markup_standard_pct}% · floor ${t.markup_floor_pct}%` +
        `${track === "backorder" ? " (below the floor needs Superadmin approval)" : " (below the floor needs a volume commitment)"}.\n` +
        `Leave blank to keep the standard.`,
        t.markup_recorded ? String(t.markup_pct) : "");
      if (v === null) return undefined;          // cancelled
      if (v === "") return null;                 // clear -> fall back to standard
      const n = Number(v);
      if (Number.isNaN(n) || n < 0 || n > 500) return NaN;
      return n;
    };
    const c = askTrack("contract", "Contracted-line");
    if (c === undefined) return;
    const b = askTrack("backorder", "Backorder");
    if (b === undefined) return;
    if (Number.isNaN(c) || Number.isNaN(b)) {
      showToast("error", "Enter a markup between 0 and 500", 4000);
      return;
    }
    return act(
      () => axios.patch(`${API}/api/admin/rebates/enrollments/${row.enrollment_id}`,
        { markup_pct_for: data.period, contract_markup_pct: c, backorder_markup_pct: b },
        auth()),
      `mg-${row.enrollment_id}`, "Markup recorded");
  };

  const preview = async (row) => {
    setBusy(`pv-${row.hospital_id}`);
    try {
      const r = await axios.get(`${API}/api/admin/rebates/statement/${row.hospital_id}`, auth());
      setStatement({ ...r.data, hospital_id: row.hospital_id });
    } catch (e) {
      showToast("error", e?.response?.data?.detail || "Could not build statement", 5000);
    } finally { setBusy(""); }
  };

  const send = (hospital_id) => act(
    () => axios.post(`${API}/api/admin/rebates/statement/send`, { hospital_id }, auth()),
    `sd-${hospital_id}`, "Statement emailed");

  const accounts = data?.accounts || [];

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10" data-testid="rebate-program-desk">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-sky-600 mb-1.5">
              Sourcing Desk · Payables
            </p>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
              Secondary Source Rebate Program
            </h1>
            <p className="text-sm text-slate-500 mt-1.5">
              {data ? `Period ${data.period} · ${data.enrolled_count} enrolled` : "Loading…"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select value={period} onChange={(e) => setPeriod(e.target.value)}
              data-testid="rebate-period-select"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 focus:border-sky-500 focus:outline-none">
              {periodOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <button onClick={load} disabled={loading} data-testid="rebate-desk-refresh"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-sky-500 transition-colors disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Recalculate
            </button>
          </div>
        </div>

        {data?.pre_launch && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-5 py-4"
            data-testid="rebate-desk-pre-launch">
            <p className="text-sm font-bold text-amber-900">
              Pre-launch · program goes live {data.launch_date || "2027-01-01"}
            </p>
            <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
              Enrollments are being accepted now and take effect {data.launch_period || "2027-Q1"}.
              Spend shown for earlier quarters is baseline volume only — nothing accrues and no
              statements are sent until January 2027 closes. First statement run: February 3, 2027.
            </p>
          </div>
        )}

        {toast && (
          <div className={`mb-6 rounded-lg border px-4 py-3 text-sm font-semibold ${
            toast.kind === "error"
              ? "border-rose-300 bg-rose-50 text-rose-800"
              : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}
            data-testid="rebate-desk-toast">{toast.msg}</div>
        )}
        {err && (
          <div className="mb-6 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800"
            data-testid="rebate-desk-error">{err}</div>
        )}

        {/* totals */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="rebate-total-spend">
            <DollarSign className="w-5 h-5 text-sky-600 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Qualified spend, period</p>
            <p className="text-2xl font-black text-slate-900 tabular-nums">
              {money(data?.totals?.qualified_spend)}
            </p>
            <p className="text-[11px] text-slate-500 tabular-nums mt-1">
              {money(data?.totals?.contract_spend)} contracted · {money(data?.totals?.backorder_spend)} backorders
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="rebate-total-accrued">
            <Target className="w-5 h-5 text-sky-600 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Rebate accrued, period</p>
            <p className="text-2xl font-black text-slate-900 tabular-nums">
              {money(data?.totals?.accrued_total)}
            </p>
            <p className="text-[11px] text-slate-500 tabular-nums mt-1">
              {money(data?.totals?.contract_amount)} + {money(data?.totals?.backorder_amount)} backorders
              {Math.round(((data?.totals?.accrued_total || 0)
                - (data?.totals?.contract_amount || 0)
                - (data?.totals?.backorder_amount || 0)) * 100) > 0
                ? ` + ${money((data.totals.accrued_total - data.totals.contract_amount - data.totals.backorder_amount))} bonuses`
                : ""}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="rebate-total-liability">
            <CheckCircle2 className="w-5 h-5 text-sky-600 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Effective rate</p>
            <p className="text-2xl font-black text-slate-900 tabular-nums">
              {data?.totals?.qualified_spend
                ? `${((data.totals.accrued_total / data.totals.qualified_spend) * 100).toFixed(2)}%`
                : "—"}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="rebate-ledger-export">
            <FileSpreadsheet className="w-5 h-5 text-sky-600 mb-3" />
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Accounting hand-off</p>
            <button onClick={downloadLedger} data-testid="rebate-ledger-csv-button"
              className="text-sm font-bold text-sky-700 underline decoration-dotted hover:text-sky-600">
              Download period ledger (CSV)
            </button>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Per-order, per-track rebate lines plus earned bonuses — the hand-off format for the
              QuickBooks merge. Covers active and pending enrollments.
            </p>
          </div>
        </div>

        {/* accounts */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rebate-accounts-table">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="py-3.5 px-5">Hospital</th>
                  <th className="py-3.5 px-5">Status</th>
                  <th className="py-3.5 px-5 text-right">Qualified spend</th>
                  <th className="py-3.5 px-5">Tier</th>
                  <th className="py-3.5 px-5">Markup / GP cost</th>
                  <th className="py-3.5 px-5 text-right">Accrued</th>
                  <th className="py-3.5 px-5 text-right">Gap / upside</th>
                  <th className="py-3.5 px-5 text-right">YTD</th>
                  <th className="py-3.5 px-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="py-10 px-5 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading accounts…
                  </td></tr>
                )}
                {!loading && !accounts.length && (
                  <tr><td colSpan={9} className="py-10 px-5 text-slate-500">
                    No hospitals enrolled yet. Enrollments arrive from the public program page.
                  </td></tr>
                )}
                {accounts.map((a) => {
                  const p = a.position || {};
                  return (
                    <tr key={a.enrollment_id} className="border-t border-slate-200"
                      data-testid={`rebate-account-${a.enrollment_id}`}>
                      <td className="py-4 px-5">
                        <p className="font-bold text-slate-900">{a.hospital_name}</p>
                        <p className="text-[11px] text-slate-400 font-mono">{a.enrollment_id}</p>
                      </td>
                      <td className="py-4 px-5">
                        <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          a.status === "active" ? "bg-emerald-100 text-emerald-700"
                            : a.status === "paused" ? "bg-slate-200 text-slate-600"
                            : "bg-amber-100 text-amber-700"}`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="py-4 px-5 text-right tabular-nums text-slate-700">
                        {a.needs_link ? <span className="text-slate-400">—</span> : (
                          <>
                            {money(p.qualified_spend)}
                            <span className="block text-[11px] text-slate-500">
                              {money(p.contract_spend)} contracted · {money(p.backorder_spend)} BO
                            </span>
                          </>
                        )}
                      </td>
                      <td className="py-4 px-5 text-slate-600">
                        {a.needs_link ? <span className="text-slate-400">—</span> : (
                          <>
                            {p.tier_label}
                            {p.contract_rate_pct != null ? (
                              <span className="block text-[11px] text-slate-400 tabular-nums">
                                {pct(p.contract_rate_pct)} contracted · {pct(p.backorder_rate_pct)} BO
                              </span>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <button onClick={() => setMargin(a)} disabled={a.needs_link || busy === `mg-${a.enrollment_id}`}
                          data-testid={`rebate-margin-${a.enrollment_id}`}
                          className="text-left group disabled:opacity-50">
                          {a.needs_link ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <>
                              {["contract", "backorder"].map((tk) => {
                                const t = a.margin?.pricing?.[tk] || {};
                                return (
                                  <span key={tk} className="block">
                                    <span className={`text-xs font-bold tabular-nums ${
                                      t.below_floor ? "text-rose-600" : "text-emerald-700"}`}>
                                      {tk === "contract" ? "CL" : "BO"} {t.markup_pct}% markup
                                    </span>
                                    <span className="text-[10px] text-slate-400">
                                      {t.markup_recorded ? "" : " (std)"}
                                      {t.margin_pct != null ? ` · ${t.margin_pct}% GM` : ""}
                                    </span>
                                  </span>
                                );
                              })}
                              <span className="block text-[11px] text-slate-500 tabular-nums mt-0.5">
                                {a.margin?.rebate_share_of_gross_profit_pct != null
                                  ? `rebate = ${a.margin.rebate_share_of_gross_profit_pct}% of GP`
                                  : "no spend yet"}
                              </span>
                              {!a.margin?.margin_floor_met && (
                                <span className="block text-[10px] font-bold uppercase tracking-wider text-rose-600 mt-0.5">
                                  Below floor · Superadmin approval
                                </span>
                              )}
                            </>
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-5 text-right tabular-nums font-bold text-slate-900">
                        {a.needs_link ? <span className="font-normal text-slate-400">—</span>
                          : money(p.accrued_total)}
                        {!a.needs_link && p.accrued_total > 0 && (
                          <span className="block text-[11px] font-normal text-slate-500 tabular-nums">
                            {money(p.contract_amount)} + {money(p.backorder_amount)} BO
                            {(p.growth_amount || 0) + (p.commitment_amount || 0) > 0
                              ? ` + ${money((p.growth_amount || 0) + (p.commitment_amount || 0))} bonus`
                              : ""}
                          </span>
                        )}
                        {!a.needs_link && !a.rebate_enabled && (
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            {p.rebate_disabled_effective
                              ? `off from ${p.rebate_disabled_effective}`
                              : "rebate off"}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-5 text-right tabular-nums text-xs">
                        {a.needs_link ? <span className="text-slate-400">—</span>
                          : p.next_tier ? (
                          <>
                            <span className="text-slate-500">{money0(p.gap_to_next_tier)} to {p.next_tier}</span>
                            <br />
                            <span className="text-sky-600 font-bold">+{money0(p.upside_if_reached)}</span>
                          </>
                        ) : <span className="text-emerald-600 font-bold">Top tier</span>}
                      </td>
                      <td className="py-4 px-5 text-right tabular-nums text-xs">
                        {a.needs_link ? <span className="text-slate-400">—</span> : (
                          <>
                            <span className="text-slate-600">{money0(a.annual?.ytd_qualified_spend)}</span>
                            <br />
                            <span className={a.annual?.annual_bonus_met ? "text-emerald-600 font-bold" : "text-slate-400"}>
                              {a.annual?.annual_bonus_met ? "$1M met" : `${money0(a.annual?.gap_to_annual_threshold)} to $1M`}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex flex-wrap gap-1.5">
                          {a.needs_link ? (
                            <button onClick={() => linkHospital(a)} disabled={busy === `lk-${a.enrollment_id}`}
                              data-testid={`rebate-link-${a.enrollment_id}`}
                              className="rounded border border-amber-400 bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-800 hover:border-amber-600 transition-colors disabled:opacity-50">
                              Link hospital account
                            </button>
                          ) : (
                          <button onClick={() => toggleRebate(a)} disabled={busy === `rb-${a.enrollment_id}`}
                            data-testid={`rebate-switch-${a.enrollment_id}`}
                            title="Turn this hospital's rebate on or off (takes effect next quarter)"
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors disabled:opacity-50 ${
                              a.rebate_enabled
                                ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                                : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                            <span className={`w-2 h-2 rounded-full ${a.rebate_enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
                            Rebate {a.rebate_enabled ? "ON" : "OFF"}
                          </button>
                          )}
                          <button onClick={() => approve(a)} disabled={busy === `st-${a.enrollment_id}`}
                            data-testid={`rebate-toggle-status-${a.enrollment_id}`}
                            className="rounded border border-slate-300 px-2.5 py-1 text-[11px] font-bold text-slate-700 hover:border-sky-500 transition-colors disabled:opacity-50">
                            {a.status === "active" ? "Pause" : "Activate"}
                          </button>
                          <button onClick={() => verify(a)} disabled={a.needs_link || busy === `cv-${a.enrollment_id}`}
                            data-testid={`rebate-verify-${a.enrollment_id}`}
                            className={`rounded border px-2.5 py-1 text-[11px] font-bold transition-colors disabled:opacity-50 ${
                              a.commitment_verified
                                ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                                : "border-slate-300 text-slate-700 hover:border-sky-500"}`}>
                            {a.commitment_verified ? "80% verified" : "Verify 80%"}
                          </button>
                          <button onClick={() => preview(a)} disabled={!a.hospital_id || busy === `pv-${a.hospital_id}`}
                            data-testid={`rebate-preview-${a.enrollment_id}`}
                            className="rounded border border-slate-300 px-2.5 py-1 text-[11px] font-bold text-slate-700 hover:border-sky-500 transition-colors disabled:opacity-50">
                            Statement
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-5 text-xs text-slate-500 max-w-3xl leading-relaxed">
          Qualified spend is the product subtotal of catalog orders on contracted lines plus
          fulfilled backorder orders; freight is excluded.
          Monthly statements go out automatically on the 3rd to every <strong>active</strong> enrollment.
        </p>

        {/* contracted lines reference */}
        <div className="mt-10 rounded-xl border border-slate-200 bg-white p-6"
          data-testid="rebate-desk-contracted-lines">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">
            Contracted lines earning the published rate
          </p>
          <p className="text-xs text-slate-500 mb-5">
            Catalog spend on these lines is credited to the contracted track. Everything else sourced
            against allocation is credited to the backorder track.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(data?.contracted_lines || []).map((l, i) => (
              <a key={l.name} href={l.url} target="_blank" rel="noreferrer"
                data-testid={`rebate-desk-line-${i}`}
                className="group rounded-lg border border-slate-200 px-4 py-3 hover:border-sky-500 transition-colors">
                <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  {l.name}
                  <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-sky-600" />
                </p>
                <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{l.category}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* statement preview */}
      {statement && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          data-testid="rebate-statement-modal">
          <div className="bg-white rounded-xl max-w-3xl w-full my-8 overflow-hidden">
            <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Statement preview</p>
                <p className="font-bold text-slate-900">{statement.subject}</p>
                <p className="text-xs text-slate-500 mt-0.5">To: {statement.recipient || "no contact email"}</p>
              </div>
              <button onClick={() => setStatement(null)} data-testid="rebate-statement-close"
                className="rounded-lg border border-slate-300 p-2 hover:border-slate-500 transition-colors">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: statement.html }} />
            <div className="border-t border-slate-200 p-5 flex justify-end gap-3">
              <button onClick={() => setStatement(null)}
                className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700">
                Close
              </button>
              <button onClick={() => { send(statement.hospital_id); setStatement(null); }}
                disabled={!statement.recipient} data-testid="rebate-statement-send"
                className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-sky-500 transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" /> Email this statement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
